import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import PageShell from '@/components/layout/PageShell';
import PageHeader from '@/components/layout/PageHeader';
import ClientCombobox from '@/components/entities/ClientCombobox';
import api from '@/lib/api';
import { toast } from 'sonner';
import { saveDraft, takeDraft, takeCreatedClientId } from '@/lib/formDraft';
import { Briefcase, ArrowLeft } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';

const emptyForm = () => ({
  title: '', client: '', client_id: '', category: '', status: 'open',
  priority: 'medium', due_date: '', assignee: '', summary: '',
});

const toForm = (c) => ({
  title: c.title || '', client: c.client || '', client_id: c.client_id || '',
  category: c.category || '', status: c.status || 'open', priority: c.priority || 'medium',
  due_date: c.due_date ? new Date(c.due_date).toISOString().slice(0, 16) : '',
  assignee: c.assignee || '', summary: c.summary || '',
});

const CaseForm = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const editing = !!id;
  const draftKey = editing ? `case:${id}` : 'case:new';

  const [attorneys, setAttorneys] = useState([]);
  const [form, setForm] = useState(emptyForm());
  const [existing, setExisting] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [ready, setReady] = useState(false);

  // Load lookup data + (for edit) the case, honouring a restored draft and a
  // just-created client when returning from the client-create page.
  const init = useCallback(async () => {
    try {
      const attorneysRes = await api.get('/attorneys').catch(() => ({ data: [] }));
      setAttorneys(attorneysRes.data);

      const draft = takeDraft(draftKey);
      let base;
      if (draft) base = draft;
      else if (editing) {
        const res = await api.get(`/cases/${id}`);
        setExisting(res.data);
        base = toForm(res.data);
      } else base = emptyForm();

      const createdId = takeCreatedClientId();
      if (createdId) {
        try {
          const c = (await api.get(`/clients/${createdId}`)).data;
          base = { ...base, client_id: c.id, client: c.display_name || '' };
        } catch { /* client fetch failed; leave unlinked */ }
      }
      setForm(base);
    } catch {
      toast.error('Failed to load case');
      navigate('/cases');
    } finally {
      setReady(true);
    }
  }, [draftKey, editing, id, navigate]);

  useEffect(() => {
    if (!loading && !user) { navigate('/login'); return; }
    if (user && !['admin', 'senior_partner', 'associate'].includes(user.role)) {
      toast.error('Access denied. Attorney privileges required.');
      navigate('/');
      return;
    }
    if (user) init();
  }, [user, loading, navigate, init]);

  const linkClient = (clientId, client) =>
    setForm((f) => ({ ...f, client_id: clientId || '', client: client?.display_name || '' }));

  // Stash the in-progress case, then go create a client and come back here.
  const createClient = () => {
    saveDraft(draftKey, form);
    const returnTo = encodeURIComponent(location.pathname + location.search);
    navigate(`/clients/new?returnTo=${returnTo}`);
  };

  const save = async () => {
    setSubmitting(true);
    try {
      const caseId = id || uuidv4();
      const payload = {
        id: caseId,
        case_number: existing?.case_number || '',
        ...form,
        due_date: form.due_date ? new Date(form.due_date).toISOString() : null,
        attachments: existing?.attachments || [],
        requester: existing?.requester || user.id,
        created_at: existing?.created_at || new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
      if (editing) {
        await api.put(`/cases/${caseId}`, payload);
        toast.success('Case updated successfully');
      } else {
        await api.post('/cases', payload);
        toast.success('Case created successfully');
      }
      navigate('/cases');
    } catch {
      toast.error('Failed to save case');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || !user || !ready) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  return (
    <PageShell>
      <PageHeader
        title={editing ? 'Edit Case' : 'New Case'}
        icon={Briefcase}
        titleTestId="case-form-title"
        actions={
          <Button variant="outline" onClick={() => navigate('/cases')} data-testid="case-form-back">
            <ArrowLeft size={18} /> Back to cases
          </Button>
        }
      />

      <Card className="max-w-2xl">
        <CardContent className="p-6 space-y-4" data-testid="case-form">
        <div>
          <Label htmlFor="case-title">Title *</Label>
          <Input id="case-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Case title" data-testid="case-title-input" />
        </div>
        <div>
          <Label htmlFor="case-client-combobox">Client *</Label>
          <ClientCombobox value={form.client_id} onChange={linkClient} onCreateNew={createClient} testId="case-client-combobox" />
        </div>
        <div>
          <Label htmlFor="case-category-select">Category *</Label>
          <Select value={form.category} onValueChange={(value) => setForm({ ...form, category: value })}>
            <SelectTrigger id="case-category-select" data-testid="case-category-select">
              <SelectValue placeholder="Select category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="litigation">Litigation</SelectItem>
              <SelectItem value="corporate">Corporate</SelectItem>
              <SelectItem value="real_estate">Real Estate</SelectItem>
              <SelectItem value="family">Family Law</SelectItem>
              <SelectItem value="criminal">Criminal Defense</SelectItem>
              <SelectItem value="immigration">Immigration</SelectItem>
              <SelectItem value="ip">Intellectual Property</SelectItem>
              <SelectItem value="employment">Employment</SelectItem>
              <SelectItem value="tax">Tax</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="case-status-select">Status</Label>
            <Select value={form.status} onValueChange={(value) => setForm({ ...form, status: value })}>
              <SelectTrigger id="case-status-select" data-testid="case-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="case-priority-select">Priority</Label>
            <Select value={form.priority} onValueChange={(value) => setForm({ ...form, priority: value })}>
              <SelectTrigger id="case-priority-select" data-testid="case-priority-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="low">Low</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="urgent">Urgent</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="case-due-date">Due Date</Label>
            <Input id="case-due-date" type="datetime-local" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="case-due-date-input" />
          </div>
          <div>
            <Label htmlFor="case-assignee-select">Assignee *</Label>
            <Select value={form.assignee} onValueChange={(value) => setForm({ ...form, assignee: value })}>
              <SelectTrigger id="case-assignee-select" data-testid="case-assignee-select">
                <SelectValue placeholder="Select assignee" />
              </SelectTrigger>
              <SelectContent>
                {attorneys.map((attorney) => (
                  <SelectItem key={attorney.id} value={attorney.id}>
                    {attorney.full_name} ({attorney.role})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <Label htmlFor="case-summary">Summary / Notes</Label>
          <Textarea id="case-summary" value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} placeholder="Case summary and notes..." rows={4} data-testid="case-summary-input" />
        </div>
        <div className="flex gap-4 pt-2">
          <Button variant="outline" onClick={() => navigate('/cases')} className="flex-1">Cancel</Button>
          <Button onClick={save} className="flex-1" disabled={!form.title || !form.client_id || !form.category || !form.assignee || submitting} data-testid="save-case-btn">
            {editing ? 'Update Case' : 'Create Case'}
          </Button>
        </div>
        </CardContent>
      </Card>
    </PageShell>
  );
};

export default CaseForm;
